import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { useCallStore, type Participant } from '@/stores/useCallStore'
import { useCallActions } from '@/hooks/useCallActions'

interface Props {
  darkMode: boolean
  /** Tighter paddings for the floating widget. */
  compact?: boolean
}

/** A person added to the call: a number, formatted; a colleague, by name. */
const nomParticipant = (p: Participant) => (p.label.startsWith('+') ? formatPhoneDisplay(p.label) : p.label)

/**
 * The conference around the call: an announced transfer under way (who the
 * agent is talking to; Hand over, Merge, Cancel), someone being added, and the
 * people already in the call with Remove. Hand over and Merge wait for the
 * colleague to answer; all wait for this phone to be back in the conference.
 *
 * Shared by `InCallPanel` and `InCallWidget`.
 */
export function AnnonceBar({ darkMode, compact = false }: Props) {
  const { t } = useTranslation()
  const annonce = useCallStore((s) => s.annonce)
  const isTransferring = useCallStore((s) => s.isTransferring)
  const retourAttendu = useCallStore((s) => s.retourAttendu)
  const participants = useCallStore((s) => s.participants)
  const ajoutEnCours = useCallStore((s) => s.ajoutEnCours)
  const { handOver, cancelAnnonce, reunir, retirer } = useCallActions()

  if (!annonce?.etape && !ajoutEnCours && participants.length === 0) return null
  const enLigne = annonce?.etape === 'en_ligne'
  const occupe = isTransferring || retourAttendu

  const boutonSecondaire = cn(
    'flex-1 h-9 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-50',
    darkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-slate-300 text-slate-700 hover:bg-white',
  )

  return (
    <div
      className={cn(
        'border-b space-y-2.5',
        compact ? 'px-3 py-2.5' : 'px-4 py-3',
        darkMode ? 'bg-amber-500/10 border-slate-800' : 'bg-amber-50 border-slate-200',
      )}
    >
      {annonce?.etape && (
        <div className="space-y-2">
          <p
            role="status"
            aria-live="polite"
            className={cn('text-xs font-medium', darkMode ? 'text-amber-300' : 'text-amber-800')}
          >
            {enLigne
              ? t('phone.transfer.announceOnLine', { name: annonce.label })
              : t('phone.transfer.announceRinging', { name: annonce.label })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handOver().catch(() => {})}
              disabled={!enLigne || occupe}
              className="flex-1 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
            >
              {t('phone.transfer.handOver')}
            </button>
            <button
              type="button"
              onClick={() => void reunir().catch(() => {})}
              disabled={!enLigne || occupe}
              title={t('phone.ajout.mergeHint', 'Le client, vous et votre collègue, ensemble')}
              className={boutonSecondaire}
            >
              {t('phone.ajout.merge', 'Réunir')}
            </button>
            <button
              type="button"
              onClick={() => void cancelAnnonce().catch(() => {})}
              disabled={occupe}
              className={boutonSecondaire}
            >
              {t('phone.transfer.cancel')}
            </button>
          </div>
        </div>
      )}

      {ajoutEnCours && (
        <p
          role="status"
          aria-live="polite"
          className={cn('flex items-center gap-2 text-xs font-medium', darkMode ? 'text-amber-300' : 'text-amber-800')}
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('phone.ajout.adding', { name: ajoutEnCours })}
        </p>
      )}

      {participants.length > 0 && (
        <div className="space-y-1">
          <p className={cn('text-[10px] font-semibold uppercase tracking-wider', darkMode ? 'text-slate-400' : 'text-slate-500')}>
            {t('phone.ajout.inCall', "Dans l'appel")}
          </p>
          {participants.map((p) => (
            <div key={p.jambe} className="flex items-center gap-2">
              <span
                className={cn(
                  'h-2 w-2 flex-shrink-0 rounded-full',
                  p.en_ligne ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse',
                )}
              />
              <span className={cn('min-w-0 flex-1 truncate text-xs', darkMode ? 'text-slate-200' : 'text-slate-800')}>
                {nomParticipant(p)}
                <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>
                  {' · '}
                  {p.en_ligne ? t('phone.ajout.onLine', 'en ligne') : t('phone.ajout.ringing', 'sonne…')}
                </span>
              </span>
              <button
                type="button"
                onClick={() => void retirer(p.jambe).catch(() => {})}
                className={cn(
                  'h-7 rounded-md border px-2.5 text-[11px] font-semibold transition-colors',
                  darkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-white',
                )}
              >
                {p.en_ligne ? t('phone.ajout.remove', 'Retirer') : t('phone.transfer.cancel')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
